import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { projectsApi } from '../../../services/api';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';

const ProjectPurposesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [showControls, setShowControls] = useState(true);
  const [projectPurposes, setProjectPurposes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Unified modal state
  const [projectPurposeModal, setProjectPurposeModal] = useState({ 
    open: false, 
    isEdit: false, 
    formData: { name: '', code: '', description: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, projectPurpose: null });

  const fetchProjectPurposes = async () => {
    try {
      setIsLoading(true);
      const data = await projectsApi.getProjectPurposes();
      setProjectPurposes(data);
    } catch (err) {
      showAlert('Failed to load project purposes.', 'error', err);
      console.error('Error fetching project purposes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectPurposes();
  }, []);

  // Unified handler for both add and edit
  const handleSaveProjectPurpose = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (projectPurposeModal.isEdit) {
        // Edit existing project purpose
        if (!projectPurposeModal.originalId) {
          showAlert('Invalid project purpose data for update', 'error');
          return;
        }
        
        await projectsApi.updateProjectPurpose(projectPurposeModal.originalId, {
          name: projectPurposeModal.formData.name,
          code: projectPurposeModal.formData.code,
          description: projectPurposeModal.formData.description || null
        });
        showAlert('Project purpose updated successfully', 'success');
      } else {
        // Add new project purpose
        await projectsApi.createProjectPurpose({
          name: projectPurposeModal.formData.name,
          code: projectPurposeModal.formData.code,
          description: projectPurposeModal.formData.description || null
        });
        showAlert('Project purpose added successfully', 'success');
      }
      
      await fetchProjectPurposes();
      // Reset modal state
      setProjectPurposeModal({
        open: false,
        isEdit: false,
        formData: { name: '', code: '', description: '' },
        originalId: null
      });
    } catch (err) {
      const action = projectPurposeModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} project purpose.`, 'error', err);
      console.error(`Error ${action}ing project purpose:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProjectPurpose = (projectPurpose) => {
    setDeleteModal({ open: true, projectPurpose });
  };

  const confirmDelete = async () => {
    const purposeToDelete = deleteModal.projectPurpose;
    setDeleteModal({ open: false, projectPurpose: null });
    
    if (!purposeToDelete?.id) {
      showAlert('Invalid project purpose data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await projectsApi.deleteProjectPurpose(purposeToDelete.id);
      await fetchProjectPurposes();
      showAlert('Project purpose deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting project purpose:', err);
      showAlert(err.message || 'Failed to delete project purpose.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProjectPurposes = projectPurposes.filter(purpose => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    return purpose.name.toLowerCase().includes(lowerSearch) || 
           (purpose.code && purpose.code.toLowerCase().includes(lowerSearch)) ||
           (purpose.description && purpose.description.toLowerCase().includes(lowerSearch));
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Project Purposes Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Project Purpose"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            {ability.can(ACTIONS.CREATE, SUBJECTS.PROJECT_PURPOSE) && (
              <HeaderButton onClick={() => setProjectPurposeModal({
                open: true,
                isEdit: false,
                formData: { name: '', code: '', description: '' },
                originalId: null
              })}>
                <FaPlus /> Add Project Purpose
              </HeaderButton>
            )}
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredProjectPurposes.length}
                </span>
                {filteredProjectPurposes.length === 1 ? 'project purpose' : 'project purposes'} found
              </div>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredProjectPurposes.map((projectPurpose) => (
                  <Grid item key={projectPurpose.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '4px'
                            }}
                          >
                            {projectPurpose.name}
                          </Typography>
                          {projectPurpose.code && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#868e96',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                display: 'block',
                                marginBottom: '8px'
                              }}
                            >
                              Code: {projectPurpose.code}
                            </Typography>
                          )}
                          {projectPurpose.description && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6c757d',
                                marginBottom: '8px'
                              }}
                            >
                              {projectPurpose.description}
                            </Typography>
                          )}
                          {projectPurpose._count?.projects > 0 && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#868e96',
                                fontStyle: 'italic'
                              }}
                            >
                              Used by {projectPurpose._count.projects} project{projectPurpose._count.projects !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          {ability.can(ACTIONS.UPDATE, SUBJECTS.PROJECT_PURPOSE) && (
                            <Button
                              onClick={() => setProjectPurposeModal({
                                open: true,
                                isEdit: true,
                                formData: { 
                                  name: projectPurpose.name,
                                  code: projectPurpose.code || '',
                                  description: projectPurpose.description || ''
                                },
                                originalId: projectPurpose.id
                              })}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#0d6efd',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(13, 110, 253, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                  color: '#0d6efd'
                                }
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {ability.can(ACTIONS.DELETE, SUBJECTS.PROJECT_PURPOSE) && (
                            <Button
                              onClick={() => handleDeleteProjectPurpose(projectPurpose)}
                              sx={{
                                minWidth: 'unset',
                                padding: '6px 12px',
                                fontSize: '14px',
                                color: '#dc3545',
                                transition: 'all 0.2s ease',
                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                  color: '#dc3545'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing project purposes */}
      <Modal
        isOpen={projectPurposeModal.open}
        onClose={() => setProjectPurposeModal({
          open: false,
          isEdit: false,
          formData: { name: '', code: '', description: '' },
          originalId: null
        })}
        onSubmit={handleSaveProjectPurpose}
        title={projectPurposeModal.isEdit ? "Edit Project Purpose" : "Add Project Purpose"}
        submitText={projectPurposeModal.isEdit ? "Update" : "Add"}
      >
        <Box mb={2}>
          <TextField
            id="project-purpose-name"
            name="name"
            label="Project Purpose Name"
            required
            value={projectPurposeModal.formData.name}
            onChange={(e) => setProjectPurposeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter project purpose name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="project-purpose-code"
            name="code"
            label="Code"
            required
            value={projectPurposeModal.formData.code}
            onChange={(e) => setProjectPurposeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter project purpose code"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="project-purpose-description"
            name="description"
            label="Description"
            value={projectPurposeModal.formData.description}
            onChange={(e) => setProjectPurposeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, description: e.target.value }
            }))}
            placeholder="Enter description (optional)"
            autoComplete="off"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
          />
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, projectPurpose: null })}
        onConfirm={confirmDelete}
        title="Delete Project Purpose"
        message={`Are you sure you want to delete the project purpose "${deleteModal.projectPurpose?.name}"? This action cannot be undone.`}
      />
    </Container>
  );
};

export default ProjectPurposesConfiguration;